import React, { useEffect, useState } from 'react';
import { homeownerPost } from '../utils/homeownerApi';
import { formatDateOnly } from '../utils/dates';
import { selectHomePrompts } from '../shared/homePrompts';
import {
  FLORIDA_EARLY_PAY_CHECKED,
  FLORIDA_EARLY_PAY_NOTICE,
  FLORIDA_EARLY_PAY_SOURCE,
  FLORIDA_EARLY_PAY_SOURCE_LABEL,
  isEarlyPayTaxItem,
  showEarlyPayTax,
} from '../shared/consentCopy';
import { showToast } from './Toast';
import PlanDateField from './PlanDateField';

const buttonStyle = (colors, brandColor, primary, disabled) => {
  const style = {
    flex: '1 1 0',
    minWidth: 0,
    minHeight: '44px',
    borderRadius: '0.5rem',
    border: primary ? 'none' : `1px solid ${colors.border || '#6b7280'}`,
    background: primary ? brandColor : (colors.surface || '#fff'),
    color: primary ? '#083838' : (colors.text || '#133'),
    fontWeight: 700,
    fontSize: '1rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: '0.5rem 0.65rem',
  };
  if (disabled) style.opacity = 0.55;
  return style;
};

function cardShell(colors) {
  return {
    marginBottom: '1rem',
    padding: '1rem',
    borderRadius: '0.75rem',
    border: `1px solid ${colors.border}`,
    background: colors.surface,
  };
}

/**
 * One home card at a time. The server decides the question or the interval offer.
 * This file does not import the scheduler.
 */
export default function HomePhaseCards({
  colors,
  brandColor = '#59c4c4',
  onChanged,
  onOpenTask,
  checkinVisible = false,
  status = null,
  state = '',
}) {
  const [card, setCard] = useState(status && status.card ? status.card : null);
  const [nextFixed, setNextFixed] = useState(status && status.nextFixed ? status.nextFixed : null);
  const [saving, setSaving] = useState(false);
  const [savingAnswer, setSavingAnswer] = useState('');
  const [policyOpen, setPolicyOpen] = useState(false);
  const [renewalDate, setRenewalDate] = useState('');
  const [termMonths, setTermMonths] = useState('12');

  const load = () => {
    homeownerPost('/.netlify/functions/homeowner-plan', { action: 'phase-status' })
      .then((result) => {
        setCard(result && result.card ? result.card : null);
        setNextFixed(result && result.nextFixed ? result.nextFixed : null);
        setPolicyOpen(false);
        setRenewalDate('');
        setTermMonths('12');
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (status) return undefined;
    load();
    return undefined;
  }, [status]);

  const finish = async (result, message) => {
    showToast(message, 'success');
    if (onChanged) await onChanged(result);
    load();
  };

  const sendQuestion = async (answer) => {
    if (!card || saving) return;
    if (answer === 'yes' && card.policy && !policyOpen) {
      setPolicyOpen(true);
      return;
    }
    if (answer === 'yes' && card.policy && !renewalDate) {
      showToast('Choose a renewal date.', 'error');
      return;
    }
    setSaving(true);
    setSavingAnswer(answer);
    try {
      const result = await homeownerPost('/.netlify/functions/homeowner-plan', {
        action: 'progressive-answer',
        key: card.key,
        answer,
        renewalDate: card.policy ? renewalDate : undefined,
        termMonths: card.policy ? Number(termMonths) : undefined,
      });
      const message = answer === 'later'
        ? 'We will ask again later.'
        : 'Saved. Your plan matches that answer.';
      await finish(result, message);
    } catch (error) {
      showToast(error && error.message ? error.message : 'Could not save that answer.', 'error');
    } finally {
      setSaving(false);
      setSavingAnswer('');
    }
  };

  const sendTune = async (accept) => {
    if (!card || saving) return;
    setSaving(true);
    setSavingAnswer(accept ? 'accept' : 'keep');
    try {
      const result = await homeownerPost('/.netlify/functions/homeowner-plan', {
        action: 'tune-answer',
        seriesId: card.seriesId,
        accept,
      });
      await finish(result, accept ? 'Updated. Future dates follow the new interval.' : 'Kept the current interval.');
    } catch (error) {
      showToast('Could not update that interval.', 'error');
    } finally {
      setSaving(false);
      setSavingAnswer('');
    }
  };

  if (checkinVisible == null) return null;

  const prompts = selectHomePrompts({
    hasCheckin: checkinVisible,
    upcoming: nextFixed,
    action: card,
  }).filter((item) => item.type !== 'checkin');
  if (!prompts.length) return null;

  return (
    <div>
      {prompts.map((item) => {
        if (item.type === 'upcoming') {
          if (isEarlyPayTaxItem(item) && !showEarlyPayTax(item, state)) return null;
          const when = formatDateOnly(item.due_date);
          return (
            <section key="upcoming" aria-label="Upcoming in your area" style={cardShell(colors)}>
              <h2 style={{ margin: '0 0 0.35rem', color: colors.text, fontSize: '1.2rem' }}>
                Upcoming in your area
              </h2>
              {showEarlyPayTax(item, state) ? (
                <>
                  <p style={{ margin: '0 0 0.35rem', color: colors.text, fontSize: '1rem', lineHeight: 1.45 }}>
                    {FLORIDA_EARLY_PAY_NOTICE}
                    {' Source: '}
                    <a href={FLORIDA_EARLY_PAY_SOURCE} target="_blank" rel="noopener noreferrer">{FLORIDA_EARLY_PAY_SOURCE_LABEL}</a>
                  </p>
                  <p style={{ margin: '0 0 0.75rem', color: colors.textSecondary, fontSize: '0.8rem', lineHeight: 1.45 }}>
                    {FLORIDA_EARLY_PAY_CHECKED}
                  </p>
                </>
              ) : (
                <>
                  <p style={{ margin: '0 0 0.2rem', color: colors.text, fontWeight: 650 }}>{item.title}</p>
                  <p style={{ margin: '0 0 0.35rem', color: colors.text }}>{when}</p>
                  <p style={{ margin: '0 0 0.75rem', color: colors.textSecondary }}>{item.why}</p>
                </>
              )}
              <button
                type="button"
                onClick={() => {
                  if (onOpenTask) onOpenTask({
                    taskId: item.task_id,
                    seriesId: item.series_id,
                    dueDate: item.due_date,
                    title: item.title,
                  });
                }}
                style={{
                  minHeight: '44px',
                  padding: '0.35rem 0',
                  border: 'none',
                  background: 'transparent',
                  color: brandColor,
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                View task
              </button>
            </section>
          );
        }
        if (item.type === 'tune') {
          return (
            <section key="tune" aria-label="Interval offer" style={cardShell(colors)}>
              <h2 style={{ margin: '0 0 0.75rem', color: colors.text, fontSize: '1.2rem' }}>{item.prompt}</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <button type="button" disabled={saving} onClick={() => sendTune(true)} style={buttonStyle(colors, brandColor, true, saving)}>
                  {saving && savingAnswer === 'accept' ? 'Saving...' : 'Accept'}
                </button>
                <button type="button" disabled={saving} onClick={() => sendTune(false)} style={buttonStyle(colors, brandColor, false, saving)}>
                  {saving && savingAnswer === 'keep' ? 'Saving...' : 'Keep current'}
                </button>
              </div>
            </section>
          );
        }
        if (item.type === 'question') {
          return (
            <section key="question" aria-label="Home question" aria-busy={saving} style={cardShell(colors)}>
              <h2 style={{ margin: '0 0 0.35rem', color: colors.text, fontSize: '1.2rem' }}>{item.prompt}</h2>
              {item.detail ? (
                <p style={{ margin: '0 0 0.75rem', color: colors.textSecondary }}>{item.detail}</p>
              ) : null}
              {policyOpen ? (
                <div style={{ marginBottom: '0.75rem' }}>
                  {item.policy === 'flood' ? (
                    <p style={{ margin: '0 0 0.5rem', color: colors.textSecondary }}>
                      New flood policies can have a 30-day wait.
                    </p>
                  ) : null}
                  <label style={{ display: 'block', fontWeight: 650, marginBottom: '0.35rem', color: colors.text }}>
                    Renewal date
                  </label>
                  <PlanDateField
                    id="question-renewal"
                    label="Renewal date"
                    value={renewalDate}
                    colors={colors}
                    disabled={saving}
                    onChange={(event) => setRenewalDate(event.target.value)}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" disabled={saving} onClick={() => setTermMonths('6')} style={buttonStyle(colors, brandColor, termMonths === '6', saving)}>
                      6 months
                    </button>
                    <button type="button" disabled={saving} onClick={() => setTermMonths('12')} style={buttonStyle(colors, brandColor, termMonths === '12', saving)}>
                      12 months
                    </button>
                  </div>
                </div>
              ) : null}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <button type="button" disabled={saving} onClick={() => sendQuestion('yes')} style={buttonStyle(colors, brandColor, false, saving)}>
                  {saving && savingAnswer === 'yes' ? 'Saving...' : (policyOpen ? 'Save' : 'Yes')}
                </button>
                <button type="button" disabled={saving} onClick={() => sendQuestion('no')} style={buttonStyle(colors, brandColor, false, saving)}>
                  {saving && savingAnswer === 'no' ? 'Saving...' : 'No'}
                </button>
                <button type="button" disabled={saving} onClick={() => sendQuestion('later')} style={buttonStyle(colors, brandColor, false, saving)}>
                  {saving && savingAnswer === 'later' ? 'Saving...' : 'Not now'}
                </button>
              </div>
            </section>
          );
        }
        return null;
      })}
    </div>
  );
}
